import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  Users,
  TrendingUp,
  Award,
  AlertTriangle,
  Calendar,
  BarChart3,
  Activity,
  BookOpen,
  Settings
} from 'lucide-react';

interface TeamAnalyticsOverviewProps {
  data: {
    teamOverview: {
      totalPlayers: number;
      activeToday: number;
      averageImprovement: number;
      topPerformers: Array<{
        id: string;
        name: string;
        avgQuality: number;
        improvement: number;
        sessions: number;
      }>;
      needsAttention: Array<{
        id: string;
        name: string;
        issue: string;
        lastSession: string;
      }>;
    };
    performanceDistribution: {
      skillLevelBreakdown: Record<string, number>;
      improvementRates: Record<string, number>;
      attendancePatterns: Record<string, number>;
    };
    coachingInsights: {
      mostEffectiveDrills: Array<{
        name: string;
        effectiveness: number;
        usageCount: number;
      }>;
      playerProgressRates: {
        averageRate: number;
        topQuartile: number;
        medianRate: number;
        improvementFactors: string[];
      };
      sessionOptimization: {
        optimalDuration: number;
        optimalFrequency: string;
        peakPerformanceTime: string;
        recommendedBreaks: number;
      };
    };
  };
  timeRange: string;
}

const COLORS = {
  primary: '#0ea5e9',
  secondary: '#10b981',
  accent: '#f59e0b',
  success: '#22c55e',
  warning: '#eab308',
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899'
};

const PIE_COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];

export function TeamAnalyticsOverview({ data }: TeamAnalyticsOverviewProps) {
  // Convert object data to chart-friendly format
  const skillLevelData = Object.entries(data.performanceDistribution.skillLevelBreakdown).map(([level, count]) => ({
    level,
    count,
    percentage: (count / data.teamOverview.totalPlayers) * 100
  }));

  const improvementData = Object.entries(data.performanceDistribution.improvementRates).map(([rate, count]) => ({
    rate,
    count,
    percentage: (count / data.teamOverview.totalPlayers) * 100
  }));

  const attendanceData = Object.entries(data.performanceDistribution.attendancePatterns).map(([pattern, count]) => ({
    pattern,
    count,
    percentage: (count / data.teamOverview.totalPlayers) * 100
  }));

  return (
    <div className="space-y-6">
      {/* Team Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Players</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.teamOverview.totalPlayers}</div>
            <p className="text-xs text-muted-foreground">
              Academy roster
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Today</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.teamOverview.activeToday}</div>
            <p className="text-xs text-muted-foreground">
              {((data.teamOverview.activeToday / data.teamOverview.totalPlayers) * 100).toFixed(1)}% of roster
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Improvement</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.teamOverview.averageImprovement}%</div>
            <p className="text-xs text-green-600">
              +{(data.teamOverview.averageImprovement - 12.1).toFixed(1)}% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Quartile Rate</CardTitle>
            <Award className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.coachingInsights.playerProgressRates.topQuartile}%</div>
            <p className="text-xs text-muted-foreground">
              High performers improvement
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers and Needs Attention */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-600" />
              Top Performers
            </CardTitle>
            <CardDescription>
              Players showing exceptional progress and quality
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.teamOverview.topPerformers.map((player, index) => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
                      <span className="text-sm font-bold text-yellow-700">#{index + 1}</span>
                    </div>
                    <div>
                      <div className="font-medium">{player.name}</div>
                      <div className="text-sm text-muted-foreground">{player.sessions} sessions</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{player.avgQuality.toFixed(1)}/10</div>
                    <div className="text-sm text-green-600">+{player.improvement}%</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Needs Attention
            </CardTitle>
            <CardDescription>
              Players requiring coaching intervention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.teamOverview.needsAttention.map((player) => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <div className="font-medium">{player.name}</div>
                    <div className="text-sm text-red-600">{player.issue}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">Last session:</div>
                    <div className="text-sm font-medium">{player.lastSession}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Distribution Charts */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Skill Level Distribution
            </CardTitle>
            <CardDescription>
              Breakdown by player skill levels
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={skillLevelData}
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="count"
                  label={({ level, percentage }) => `${level}: ${percentage.toFixed(0)}%`}
                  labelLine={false}
                >
                  {skillLevelData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {skillLevelData.map((item, index) => (
                <div key={item.level} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                    />
                    <span>{item.level}</span>
                  </div>
                  <span className="font-medium">{item.count} players</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Improvement Rates
            </CardTitle>
            <CardDescription>
              Player improvement distribution
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={improvementData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="rate" 
                  stroke="#6b7280"
                  fontSize={10}
                />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  formatter={(value) => [`${value} players`, 'Count']}
                />
                <Bar 
                  dataKey="count" 
                  fill={COLORS.secondary} 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Attendance Patterns
            </CardTitle>
            <CardDescription>
              Session attendance breakdown
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="pattern" 
                  stroke="#6b7280"
                  fontSize={10}
                />
                <YAxis stroke="#6b7280" />
                <Tooltip 
                  formatter={(value) => [`${value} players`, 'Count']}
                />
                <Bar 
                  dataKey="count" 
                  fill={COLORS.purple} 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Coaching Insights */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-indigo-600" />
              Most Effective Drills
            </CardTitle>
            <CardDescription>
              Training exercises with highest impact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.coachingInsights.mostEffectiveDrills.map((drill, index) => (
                <div key={drill.name} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full">
                      <span className="text-sm font-bold text-indigo-700">#{index + 1}</span>
                    </div>
                    <div>
                      <div className="font-medium">{drill.name}</div>
                      <div className="text-sm text-muted-foreground">{drill.usageCount} uses</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{drill.effectiveness.toFixed(1)}%</div>
                    <div className="text-sm text-indigo-600">effectiveness</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-600" />
              Session Optimization
            </CardTitle>
            <CardDescription>
              Data-driven coaching recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid gap-3">
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="font-medium">Optimal Duration</span>
                  <span className="text-orange-700">{data.coachingInsights.sessionOptimization.optimalDuration} minutes</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="font-medium">Optimal Frequency</span>
                  <span className="text-orange-700">{data.coachingInsights.sessionOptimization.optimalFrequency}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="font-medium">Peak Performance</span>
                  <span className="text-orange-700">{data.coachingInsights.sessionOptimization.peakPerformanceTime}</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                  <span className="font-medium">Recommended Breaks</span>
                  <span className="text-orange-700">{data.coachingInsights.sessionOptimization.recommendedBreaks}</span>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="font-medium mb-2">Key Success Factors</h4>
                <div className="space-y-1">
                  {data.coachingInsights.playerProgressRates.improvementFactors.map((factor, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      <span>{factor}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}